import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  styled,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'

import { FileRemove } from 'elements/FileUpload/common/commonInterfaces'
import { concatToSXProp } from 'utils/style'
import truncateText from 'utils/truncateText'

import { imagePreviewBox } from '../common/styles'

interface ImagePreviewProps extends FileRemove {
  src: string
  name: string
  isOnline: boolean
  tag?: string
  tagLabel?: string
  tagOptions?: Array<{ value: string; label: string }>
  onTagChange?: (value: string) => void
}

const Image = styled('img')(() => ({
  width: '100%',
  aspectRatio: '1/1',
  borderRadius: 8,
  objectFit: 'cover',
}))

const styles: Record<string, SxProps<Theme>> = {
  wrapGrid: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  imgText: {
    padding: 0.5,
    fontSize: '11px !important',
    wordBreak: 'break-word',
  },
  removeButtonGrid: {
    position: 'absolute',
    top: '-12px',
    right: '-12px',
    minWidth: 'unset',
    padding: 0,
    backgroundColor: 'rgba(255,255,255, 0.8) !important',
    borderRadius: '50%',
    color: (theme) => theme.palette.text.secondary,
  },
  wrapRow: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr auto', sm: 'minmax(0,1fr) 200px auto' },
    columnGap: { xs: 1, sm: 1 },
    rowGap: { xs: 2, sm: 0 },
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'grey.300',
    borderRadius: 2,
    p: { xs: 1, sm: 1.5 },
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    minWidth: 0,
    flex: '1 1 0%',
  },
  name: {
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    flexGrow: 1,
  },
  selectWrap: {
    gridColumn: { xs: '1 / -1', sm: '2 / 3' },
    gridRow: { xs: 2, sm: 'auto' },
    width: { xs: '100%', sm: 200 },
    ml: { xs: 0, sm: 1 },
    mr: 0,
  },
  removeBtn: {
    gridColumn: { xs: 2, sm: 3 },
    gridRow: { xs: 1, sm: 'auto' },
    justifySelf: 'end',
    alignSelf: 'start',
  },
}

export default function ImagePreview({
  src,
  onRemove,
  isOnline,
  name,
  tag = '',
  tagLabel = 'Image Type (Optional)',
  tagOptions,
  onTagChange,
}: ImagePreviewProps): JSX.Element {
  const {
    wrapGrid,
    imgText,
    removeButtonGrid,
    wrapRow,
    left,
    name: nameSx,
    selectWrap,
    removeBtn,
  } = styles

  if (Array.isArray(tagOptions) && tagOptions.length > 0) {
    return (
      <Box sx={wrapRow}>
        <Box sx={left}>
          {isOnline ? (
            <img
              src={src}
              alt={name}
              style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }}
            />
          ) : (
            <Typography sx={imgText}>{truncateText(name, 20)}</Typography>
          )}
          <Box sx={nameSx}>{name}</Box>
        </Box>

        <Box sx={selectWrap}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>{tagLabel}</InputLabel>
            <Select
              value={tag}
              label={tagLabel}
              onChange={(e) => onTagChange?.(e.target.value)}
              displayEmpty
              renderValue={(val) => (val ? String(val) : 'Select type…')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {tagOptions?.map((opt) => (
                <MenuItem key={opt.value ?? opt.label} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <IconButton onClick={onRemove} color="error" sx={removeBtn}>
          <HighlightOffIcon />
        </IconButton>
      </Box>
    )
  }

  return (
    <Box className="file-upload-file" sx={concatToSXProp(imagePreviewBox, wrapGrid)}>
      {isOnline ? (
        <Image src={src} alt={name} />
      ) : (
        <Typography sx={imgText}>{truncateText(name, 28)}</Typography>
      )}
      <Button onClick={onRemove} sx={removeButtonGrid}>
        <HighlightOffIcon />
      </Button>
    </Box>
  )
}
